import React, { useEffect, useState, useMemo, useCallback } from "react";
import { getApi } from "../../../Services/apiService";
import { getMemberDobTimestamp } from "../../../Utils/family-tree/dateAndTime";
import { getFamilyTreeIdFromStorage } from "../../../Utils/headOfLineRole";
import {
  getMultipleBirthSuperscriptText,
  getMultipleBirthLegendTags,
} from "../../../Utils/dashboardMultipleBirthSuperscript";
import {
  formatHeadOfLineFullDisplayName,
} from "../../../Utils/dashboardMemberDisplayName";
import {
  DashboardPhotoModal,
  DashboardPhotoName,
  DashboardNameMarkerGroup,
  DashboardFamilyLineColorLink,
  getDashboardMemberPhotoUrl,
  useDashboardPhotoModal,
} from "./DashboardPhotoViewer";
import DashboardBackButton from "./DashboardBackButton";
import DashboardDetailSortFilterBar from "./DashboardDetailSortFilterBar";
import DashboardMarkerLegend from "./DashboardMarkerLegend";
import DashboardPhotoTapHint from "./DashboardPhotoTapHint";
import {
  applyDashboardMemberFilterToggle,
  getDashboardBaseSortForMemberFilter,
  filterDashboardMembersByMemberFilter,
} from "../../../Utils/dashboardDetailMemberFilter";
import { sortDashboardMembersByDeathDate } from "../../../Utils/dashboardFamilyLineNumbers";

function HeadsOfLineColors() {
  const { photos: photoModalPhotos, openPhoto, closePhoto } = useDashboardPhotoModal();
  const [headsOfLine, setHeadsOfLine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nameColumnWidth, setNameColumnWidth] = useState("auto");
  const [sortBy, setSortBy] = useState("oldest"); // "none", "alpha-asc", "alpha-desc", "youngest", "oldest", "color", "death-asc"
  const [memberFilter, setMemberFilter] = useState("all");
  const [isCompactLayout, setIsCompactLayout] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 768
  );
  const isLargeView = false;

  // A member tab on the right of the bar's divider is selected on its own: it
  // clears every sort on the left, and the list is ordered by the tab's own
  // order below without a sort button lit for it.
  //
  // The sort side effect runs here in the click handler, not inside a
  // setMemberFilter updater: updaters run in the render phase, where React does not
  // reliably apply a setState for another piece of state — the list sorted but the
  // sort bar never lit the matching button up.
  const toggleMemberFilter = (filter) => {
    setMemberFilter(
      applyDashboardMemberFilterToggle(memberFilter, filter, setSortBy)
    );
  };

  // What the list is actually ordered by. With no sort lit, that is the order
  // the active member tab is read in, falling back to this page's own order:
  // heads of line read oldest first.
  const effectiveSort =
    sortBy === "none"
      ? getDashboardBaseSortForMemberFilter(memberFilter, "oldest")
      : sortBy;

  // Fetch dashboard statistics with details to get heads of line
  useEffect(() => {
    const fetchHeadsOfLine = async () => {
      try {
        setLoading(true);
        const family_tree_id = getFamilyTreeIdFromStorage();

        if (!family_tree_id) {
          setHeadsOfLine([]);
          setLoading(false);
          return;
        }

        const response = await getApi(`/dashboard-statistics-with-details?family_tree_id=${family_tree_id}`);
        
        if (response?.statusCode === 200 && response?.data) {
          // Extract heads of line details from the response
          // The API returns: data.heads_of_line.details (array)
          const headsOfLineData = 
            response?.data?.heads_of_line?.details || 
            response?.data?.family_lines?.details ||
            response?.data?.heads_of_line || 
            response?.data?.family_lines || 
            response?.data?.headsOfLine ||
            [];
          
          // Ensure it's always an array
          if (Array.isArray(headsOfLineData)) {
            setHeadsOfLine(headsOfLineData);
          } else {
            console.warn("Heads of line data is not an array:", headsOfLineData);
            setHeadsOfLine([]);
          }
        } else {
          setHeadsOfLine([]);
        }
      } catch (error) {
        console.error("Error fetching heads of line:", error);
        setHeadsOfLine([]);
      } finally {
        setLoading(false);
      }
    };

    fetchHeadsOfLine();
  }, []);

  useEffect(() => {
    const updateLayoutMode = () => {
      setIsCompactLayout(window.innerWidth <= 768);
    };

    updateLayoutMode();
    window.addEventListener("resize", updateLayoutMode);

    return () => window.removeEventListener("resize", updateLayoutMode);
  }, []);

  // Helper function to get member name
  const getMemberName = useCallback((member) => {
    const firstName = (member?.first_name || member?.firstName || "").trim();
    if (!firstName) return "Unnamed Member";
    return formatHeadOfLineFullDisplayName(member) || "Unnamed Member";
  }, []);

  const getMemberLineColor = (member) =>
    (member?.family_line_color || member?.color_name || "").toLowerCase();

  const filteredHeadsOfLine = useMemo(
    () => filterDashboardMembersByMemberFilter(headsOfLine, memberFilter),
    [headsOfLine, memberFilter]
  );

  // Sort heads of line based on the effective sort
  const sortedHeadsOfLine = useMemo(() => {
    if (!filteredHeadsOfLine.length || effectiveSort === "none") return filteredHeadsOfLine;
    
    const sorted = [...filteredHeadsOfLine];

    if (effectiveSort === "death-asc") {
      return sortDashboardMembersByDeathDate(sorted, {
        appendDescendantSpouse: false,
      });
    }
    
    if (effectiveSort === "alpha-asc") {
      return sorted.sort((a, b) => {
        const nameA = getMemberName(a).toLowerCase();
        const nameB = getMemberName(b).toLowerCase();
        return nameA.localeCompare(nameB);
      });
    } else if (effectiveSort === "alpha-desc") {
      return sorted.sort((a, b) => {
        const nameA = getMemberName(a).toLowerCase();
        const nameB = getMemberName(b).toLowerCase();
        return nameB.localeCompare(nameA);
      });
    } else if (effectiveSort === "youngest") {
      return sorted.sort((a, b) => {
        const dobA = getMemberDobTimestamp(a);
        const dobB = getMemberDobTimestamp(b);

        if (dobA === -1 && dobB === -1) return 0;
        if (dobA === -1) return 1;
        if (dobB === -1) return -1;
        return dobB - dobA; // Later DOB first (youngest first)
      });
    } else if (effectiveSort === "oldest") {
      return sorted.sort((a, b) => {
        const dobA = getMemberDobTimestamp(a);
        const dobB = getMemberDobTimestamp(b);

        if (dobA === -1 && dobB === -1) return 0;
        if (dobA === -1) return 1;
        if (dobB === -1) return -1;
        return dobA - dobB; // Earlier DOB first (oldest first)
      });
    } else if (effectiveSort === "color") {
      return sorted.sort((a, b) => {
        const colorA = getMemberLineColor(a);
        const colorB = getMemberLineColor(b);
        if (colorA !== colorB) {
          return colorA.localeCompare(colorB);
        }
        const nameA = getMemberName(a).toLowerCase();
        const nameB = getMemberName(b).toLowerCase();
        return nameA.localeCompare(nameB);
      });
    }
    
    return sorted;
  }, [filteredHeadsOfLine, effectiveSort, getMemberName]);

  // Footer legend explains the row markers — the angel for deceased and the
  // superscript for multiple births — followed by the line colours in play.
  const multipleBirthTags = getMultipleBirthLegendTags(sortedHeadsOfLine);


  // Helper function to render head row with two columns
  const renderHeadRow = (head) => {
    const headName = getMemberName(head);
    const hexCode = head?.hex_code || "#000000";
    const colorName = head?.color_name || "";
    const isDeceased = head?.present_or_deceased === 2;
    const multipleBirthSup = getMultipleBirthSuperscriptText(head);

    // Calculate sizes based on large view mode
    const dotSize = isLargeView ? '10px' : '7px';
    const dotMarginLeft = isLargeView ? '6px' : '3px';
    const dotMarginRight = isLargeView ? '14px' : '10px';
    const iconSize = isLargeView ? '18px' : '12px';

    return (
      <div className={`dashboard-detail-row ${isLargeView ? 'large-view' : ''}`}>
        {/* Column 1: Head Name — tinted with its line color */}
        <div
          className={`dashboard-detail-name-col ${isLargeView ? "large-view" : ""}`}
          style={{ width: nameColumnWidth }}
        >
          <DashboardNameMarkerGroup>
          <DashboardPhotoName
            imageUrl={getDashboardMemberPhotoUrl(head)}
            onViewPhoto={openPhoto}
          >
            {headName}
          </DashboardPhotoName>
          {isDeceased && (
            <span className={`dashboard-detail-angel-icon ${isLargeView ? 'large-view' : ''}`}>
              <img 
                src={window.getPublicUrl("/assets/images/angel.png")} 
                alt="deceased"
                style={{ width: iconSize, height: iconSize }}
              />
            </span>
          )}
          {multipleBirthSup && (
            <sup className={`dashboard-multiple-birth-sup${isLargeView ? " large-view" : ""}`}>{multipleBirthSup}</sup>
          )}
          </DashboardNameMarkerGroup>
        </div>
        
        {/* Column 2: Color Line — this page shows the colour in its own column at
            every width, so no colour legend in the footer. Compact drops the dot and
            shows the tinted line name on its own. */}
        <div
          className={`heads-of-line-color-col ${isLargeView ? "large-view" : ""}`}
          style={{
            marginLeft: isLargeView ? "5ch" : "3ch",
          }}
        >
          {colorName && (
            <DashboardFamilyLineColorLink
              hexCode={hexCode}
              colorName={`${colorName} Line`}
              navigationMemberId={head?.id}
              className={isLargeView ? "large-view large-view-text" : ""}
              showDot={!isCompactLayout}
              dotSize={dotSize}
              dotMarginLeft={dotMarginLeft}
              dotMarginRight={dotMarginRight}
              title={`View ${colorName} Line in Family Tree`}
            />
          )}
        </div>
      </div>
    );
  };

  // Measure the actual width of name elements after render to align color line column
  useEffect(() => {
    if (sortedHeadsOfLine.length === 0 || loading) return;
    
    // Helper function to format head name (local copy for useEffect)
    const getMemberNameLocal = (member) => {
      const firstName = (member?.first_name || member?.firstName || "").trim();
      if (!firstName) return "Unnamed Member";
      return formatHeadOfLineFullDisplayName(member) || "Unnamed Member";
    };
    
    // Use appropriate font size based on view mode — compact lists render at 16px
    const fontSize = isLargeView ? "24px" : isCompactLayout ? "16px" : "18px";
    const iconSize = isLargeView ? 18 : 12;
    
    // Create temporary elements for measurement
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.visibility = 'hidden';
    tempDiv.style.whiteSpace = 'nowrap';
    tempDiv.style.fontSize = fontSize;
    tempDiv.style.fontFamily = 'inherit';
    document.body.appendChild(tempDiv);
    
    const chMeasure = document.createElement('div');
    chMeasure.style.position = 'absolute';
    chMeasure.style.visibility = 'hidden';
    chMeasure.style.fontSize = fontSize;
    chMeasure.style.fontFamily = 'inherit';
    chMeasure.textContent = '0';
    document.body.appendChild(chMeasure);
    const chWidth = chMeasure.offsetWidth;

    let maxWidth = 0;
    sortedHeadsOfLine.forEach((head) => {
      const headName = getMemberNameLocal(head);
      const isDeceased = head?.present_or_deceased === 2;
      const supText = getMultipleBirthSuperscriptText(head);

      if (!isDeceased && !supText) {
        tempDiv.textContent = headName;
        const width = tempDiv.offsetWidth;
        if (width > maxWidth) maxWidth = width;
        return;
      }

      tempDiv.innerHTML = "";
      const nameSpan = document.createElement("span");
      nameSpan.textContent = headName;
      tempDiv.appendChild(nameSpan);

      if (isDeceased) {
        const iconWrap = document.createElement("span");
        iconWrap.style.display = "inline-block";
        iconWrap.style.width = `${iconSize}px`;
        iconWrap.style.height = `${iconSize}px`;
        iconWrap.style.verticalAlign = "middle";
        tempDiv.appendChild(iconWrap);
      }

      if (supText) {
        const sup = document.createElement("sup");
        sup.className = "dashboard-multiple-birth-sup";
        sup.textContent = supText;
        sup.style.fontSize = "0.75em";
        tempDiv.appendChild(sup);
      }

      const width = tempDiv.offsetWidth;
      if (width > maxWidth) maxWidth = width;
    });
    
    document.body.removeChild(tempDiv);
    document.body.removeChild(chMeasure);
    
    const widthInCh = Math.ceil(maxWidth / chWidth);
    setNameColumnWidth(`${widthInCh}ch`);
  }, [sortedHeadsOfLine, loading, isLargeView, isCompactLayout]);

  return (
    <div className={`dashboard-container heads-of-line-colors-page dashboard-detail-page ${isLargeView ? 'large-view-mode' : ''}`}>
      <div className="dash-box dashboard-detail-box">
        <div className="dashboard-detail-header">
          <div className="dashboard-detail-header-left">
          <DashboardBackButton />
            <h3 className="dashboard-detail-title">Heads of Line/Color</h3>
            <div className="dashboard-detail-count">
              {filteredHeadsOfLine.length}
            </div>
            {!loading && headsOfLine.length > 0 && (
              <DashboardDetailSortFilterBar
                className="dashboard-detail-sort-toggle"
                buttonClassName="dashboard-detail-sort-btn"
                sortBy={sortBy}
                onSortChange={setSortBy}
                memberFilter={memberFilter}
                onMemberFilterToggle={toggleMemberFilter}
                showColorSort={false}
                showMemberFilters={false}
                showLivingDeceasedFilters
              />
            )}
          </div>
        </div>

        {loading ? (
          <div className="dashboard-detail-empty-state">
            <p>Loading heads of line...</p>
          </div>
        ) : headsOfLine.length === 0 ? (
          <div className="dashboard-detail-empty-state">
            <p>No data for this period.</p>
          </div>
        ) : filteredHeadsOfLine.length === 0 ? (
          <div className="dashboard-detail-empty-state">
            <p>No data for this period.</p>
          </div>
        ) : (
          <div className="heads-of-line-colors-page dashboard-detail-content-wrapper">
            <h4 className="heads-of-line-section-title dashboard-section-heading">Children of the Top/Start of the Line</h4>
            <div className="heads-of-line-list">
              {Array.isArray(sortedHeadsOfLine) && sortedHeadsOfLine.map((head, index) => (
                <div key={head?.id || index}>
                  {renderHeadRow(head)}
                </div>
              ))}
            </div>
            <div className="heads-of-line-colors-page in-law-footer">
              <DashboardMarkerLegend multipleBirthTags={multipleBirthTags} />
            </div>
          </div>
        )}
      </div>
      <DashboardPhotoModal photos={photoModalPhotos} onClose={closePhoto} />
      <DashboardPhotoTapHint />
    </div>
  );
}

export default HeadsOfLineColors;

